import { CardContent, FormHelperText, Grid, TextField } from '@mui/material'
import React from 'react'
import { type ConnectedProps, connect } from 'react-redux'
import { type AllTenantsState } from '../../redux/getAllTenantsSlice'
import { fetchWrapRegularKeks, fetchTenants } from '../../redux/actionCreator'
import { type RootState } from '../../../../store'
import { HandleError } from '../handleError'
import LanguageStrings from '../../../../i18n/locales'
import { isValidLabelText } from '../../../../utils/validator'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { type RegularLabelsState } from '../../redux/hybrid/getRegularLabelsSlice'

const RegularKekInfoStrings = LanguageStrings().HSMConfiguration.CreateRegularKekComponent
const HybridKekInfoStrings = LanguageStrings().HSMConfiguration.CreateHybridKekComponent

const textStyle = {
  root: {
    backgroundColor: '#272727',
    '& .MuiInputBase-input': {
      color: '#F0F0F0',
      backgroundColor: '#404040'
    },
    '& .MuiInputLabel-root': {
      color: '#F0F0F0',
      '&.Mui-focused': {
        color: '#F0F0F0'
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#00000000'
      },
      '&:hover fieldset': {
        borderColor: '#00000000'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00000000'
      }
    }
  }
}

interface ParentProps {
  label: string
  setLabel: React.Dispatch<React.SetStateAction<string>>
  regularLabel: Option | null
  setRegularLabel: React.Dispatch<React.SetStateAction<Option | null>>
  tenant: Option | null
  setTenant: React.Dispatch<React.SetStateAction<Option | null>>
  error: boolean
  setError: React.Dispatch<React.SetStateAction<boolean>>
  nameErrorInit: boolean
  setNameErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  regularLabelOptions: Option[]
  setRegularLabelOptions: React.Dispatch<React.SetStateAction<Option[]>>
  tenantOptions: Option[]
  setTenantOptions: React.Dispatch<React.SetStateAction<Option[]>>
}

const HybridKekInformation = (props: AllProps): JSX.Element => {
  React.useEffect(() => {
    if (isValidLabelText(props.label) && props.label.length <= 60) {
      props.setError(false)
    } else {
      props.setError(true)
    }
  }, [props.label])

  React.useEffect(() => {
    if (!(props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0)) {
      props.fetchTenants()
    }
  }, [])

  React.useEffect(() => {
    if (props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0) {
      const tenants: Option[] = []
      props.tenantTypes.tenants.forEach((tenant) => {
        tenants.push({
          value: tenant.toString(),
          label: tenant.toString()
        })
      })
      props.setTenantOptions(tenants)
    }
  }, [props.tenantTypes.tenants])

  React.useEffect(() => {
    if (!(props.regularLabels.data !== null && props.regularLabels.data.length > 0)) {
      props.fetchRegularLabels()
    }
  }, [])

  React.useEffect(() => {
    if (props.regularLabels.data !== null && props.regularLabels.data.length > 0) {
      const regularLabels: Option[] = []
      props.regularLabels.data.forEach((regularLabel: { toString: () => any }) => {
        regularLabels.push({
          value: regularLabel.toString(),
          label: regularLabel.toString()
        })
      })
      props.setRegularLabelOptions(regularLabels)
    }
  }, [props.regularLabels.data])

  const handleGroupInformationWithFailure = (): JSX.Element => {
    if (props.tenantTypes.isLoading) {
      return (
        HandleError({
          isLoading: true,
          unauthorized: false,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else if (props.tenantTypes.httpStatus === 401) {
      return (
        HandleError({
          isLoading: false,
          unauthorized: true,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else if (props.tenantTypes.httpStatus === 403) {
      return (
        HandleError({
          isLoading: false,
          unauthorized: true,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else {
      return (
        HandleError({
          isLoading: false,
          unauthorized: false,
          forbidden: false,
          noDataPresent: false,
          retryHandler: props.fetchTenants
        })
      )
    }
  }

  const handleRegularLabelSelect = (selectedValue: Option): void => {
    props.setRegularLabel(selectedValue)
  }

  const handleTenantSelect = (selectedValue: Option): void => {
    props.setTenant(selectedValue)
  }

  const isNameNotValid = (): boolean => {
    return (!isValidLabelText(props.label) || (props.label.length > 60))
  }

  if (props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0) {
    return (
            <>
                <CardContent>
                    <p id='group-info-title'>{RegularKekInfoStrings.EnterDetails}</p>
                    <Grid container spacing={2}>
                        <Grid item md={4}>
                            <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{RegularKekInfoStrings.Label}<sup style={{ color: '#EE3426' }}>*</sup></span>
                            <TextField
                                id='custom-textfield'
                                variant="outlined"
                                data-testid='group-name'
                                placeholder={RegularKekInfoStrings.Enter}
                                InputLabelProps={{
                                  disabled: true
                                }}
                                value={props.label}
                                onChange={(e) => {
                                  props.setLabel(e.target.value)
                                  if (!props.nameErrorInit) {
                                    props.setNameErrorInit(true)
                                  }
                                }}
                                size='small'
                                autoComplete='off'
                                className='custom-textfield'
                                sx={{
                                  ...textStyle.root,
                                  width: '60%'
                                  // border: (isParameterInvalid(row) && parameterListErrorInit.includes(row.parameterName)) ? '1px solid #EE3426' : 'none'
                                }}
                            />
                            {(isNameNotValid() && props.nameErrorInit) && (
                                // istanbul ignore next
                                <FormHelperText sx={{ color: '#EE3426', paddingLeft: '10px', width: '70%' }} className="errorColor">
                                    {RegularKekInfoStrings.LabelError}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item md={4}>
                            <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{RegularKekInfoStrings.SelectTenant}<sup style={{ color: '#EE3426' }}>*</sup></span>
                            <GenXDropdown placeholder={RegularKekInfoStrings.SelectTenant} value={props.tenant} options={props.tenantOptions} onSelect={handleTenantSelect} />
                        </Grid>
                        <Grid item md={4}>
                            <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{HybridKekInfoStrings.SelectRegularLabel}<sup style={{ color: '#EE3426' }}>*</sup></span>
                            <GenXDropdown placeholder={RegularKekInfoStrings.SelectAnOption} value={props.regularLabel} options={props.regularLabelOptions} onSelect={handleRegularLabelSelect} />
                        </Grid>
                    </Grid>
                </CardContent>
            </>
    )
  } else {
    return handleGroupInformationWithFailure()
  }
}

interface DispatchToProps {
  fetchTenants: () => void
  fetchRegularLabels: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchTenants: () => dispatch(fetchTenants()),
  fetchRegularLabels: () => dispatch(fetchWrapRegularKeks())
})

interface StateToProps {
  tenantTypes: AllTenantsState
  regularLabels: RegularLabelsState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  tenantTypes: state.getAllTenantsSlice,
  regularLabels: state.regularLabelsSlice
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

type AllProps = PropsFromRedux & ParentProps

export default connector(HybridKekInformation)
